import { useState } from 'react';
import { BookOpen, Mail, Map, Users, BookMarked, ArrowLeft } from 'lucide-react';

interface AboutProps {
  onBack: () => void;
}

export function About({ onBack }: AboutProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formData = new FormData(e.currentTarget);
      const response = await fetch('https://formspree.io/f/xpwwopoq', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setStatus('success');
                  setMessage("Thanks for your message! We'll get back to you soon.");
        (e.target as HTMLFormElement).reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Oops! There was a problem sending your message. Please try again.');
    }
  };

  return (
    <main className="min-h-screen bg-zinc-900 p-8" role="main">
      <div className="max-w-6xl mx-auto font-mono relative">
        <button 
          onClick={onBack}
          className="group mb-8 flex items-center text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
          Back
        </button>
        
        <header className="mb-16">
          <div className="flex mb-4">
            <h1 className="text-5xl font-light text-zinc-100 tracking-tight">About</h1>
          </div>
          <div className="flex space-x-8 text-sm text-zinc-400">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4" />
              <span>Last Updated: January 2025</span>
            </div>
          </div>
        </header>

        <div className="max-w-2xl space-y-12 text-zinc-300 leading-relaxed">
          <div className="space-y-4">
            <h2 className="text-2xl text-zinc-100 font-light">Discovering Literary Havens</h2>
            <p className="text-justify">
              In a world of one-click purchases, local bookstores remain irreplaceable treasures. They're not just 
              shops—they're community anchors, cultural landmarks, and portals to countless worlds bound in paper 
              and ink. Each store tells its own story, curated by passionate booksellers who transform shelves 
              into experiences.
            </p>
            
            <p className="text-justify">
              This project maps these literary sanctuaries across cities, helping readers discover unique 
              bookshops in their neighborhood and beyond. Whether you're a local looking for your next reading 
              nook or a traveler seeking literary adventures, we're here to guide you to your next favorite bookstore.
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl text-zinc-100 font-light">Our Mission</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start gap-3">
                <Map className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-zinc-100 font-medium mb-2">Mapping Literary Culture</h3>
                  <p className="text-justify text-sm text-zinc-400">
                    Visualizing the rich tapestry of independent and chain bookstores that make each city unique.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-zinc-100 font-medium mb-2">Supporting Communities</h3>
                  <p className="text-justify text-sm text-zinc-400">
                    Helping readers discover and support local bookstores that enrich our neighborhoods.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BookMarked className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-zinc-100 font-medium mb-2">Preserving Culture</h3>
                  <p className="text-justify text-sm text-zinc-400">
                    Documenting and celebrating these vital spaces that keep literary culture alive in our cities.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-zinc-100 font-medium mb-2">Inspiring Exploration</h3>
                  <p className="text-sm text-zinc-400">
                    Creating tools for book lovers to explore and discover new literary spaces.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl text-zinc-100 font-light">Join the Journey</h2>
            <p className="text-justify">
              This is a living project that grows with your contributions. Know a hidden gem we've missed? 
              Found a new bookstore that just opened? Help us keep this map accurate and comprehensive by 
              sharing your insights below.
            </p>
            
            <p className="text-sm text-zinc-400">
              Data last updated: January 2025.
            </p>
          </div>

          <div className="mt-12 pt-12 border-t border-zinc-800">
            <div className="flex items-center gap-2 mb-6">
              <Mail className="w-4 h-4 text-orange-500" />
              <h2 className="text-lg text-zinc-100">Share Your Insights</h2>
            </div>
            
            <p className="text-sm text-zinc-400 mb-6">
              Found a new bookstore? Spotted an error? Have a suggestion? Your input helps make this resource 
              better for everyone.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-zinc-400 mb-2">
                  Your email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full bg-zinc-800 text-zinc-100 px-4 py-2 rounded-none 
                           border border-zinc-700 focus:border-orange-500 focus:ring-1 
                           focus:ring-orange-500 outline-none transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-zinc-400 mb-2">
                  Your message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  className="w-full bg-zinc-800 text-zinc-100 px-4 py-2 rounded-none 
                           border border-zinc-700 focus:border-orange-500 focus:ring-1 
                           focus:ring-orange-500 outline-none transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="px-6 py-2 bg-orange-500 text-zinc-900 rounded-none 
                         hover:bg-orange-400 transition-colors disabled:opacity-50 
                         disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>

              {message && (
                <div 
                  className={`text-sm ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}
                  role="alert"
                >
                  {message}
                </div>
              )}
            </form>
          </div>

          <p className="pt-8 text-sm text-zinc-400">
            Created by{' '}
            <a 
              href="https://giorgiobaldelli.com/" 
              className="hover:text-zinc-300 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Giorgio Baldelli
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}